<template>
  <div class="c-company-actions-cell-renderer">
    <template v-if="params.context?.canManageCompanies">
      <div v-if="status === COMPANY_STATUS.PENDING">
        <GuButton
          aria-label="Approve Company"
          class="me-2"
          icon="bx-check"
          size="sm"
          variant="success"
          @click="triggerAction('approve')"
        >
          Approve
        </GuButton>

        <GuButton
          aria-label="Reject Company"
          icon="bx-block"
          size="sm"
          variant="danger"
          @click="triggerAction('reject')"
        >
          Reject
        </GuButton>
      </div>

      <div v-else-if="status === COMPANY_STATUS.ENABLED">
        <GuDialog v-model:open="isConfirmationDialogOpen">
          <GuDialogTrigger as-child>
            <GuButton
              aria-label="Disable Company"
              icon="bx-block"
              size="sm"
              variant="danger"
              outline
            >
              Disable
            </GuButton>
          </GuDialogTrigger>

          <GuDialogContent>
            <template #title>
              <h5 class="modal-title fs-18">Disable Company</h5>
            </template>

            <p>Disabling the company <strong>{{ params.data.name }}</strong> will revoke portal access for all of its members.</p>
            <p>Are you sure you want to complete this action?</p>

            <template #footer>
              <GuDialogClose as-child>
                <GuButton
                  variant="secondary"
                  :disabled="actionInProgress"
                >
                  Cancel
                </GuButton>
              </GuDialogClose>

              <GuButton
                :loading="actionInProgress"
                @click="triggerAction('disable')"
              >
                Confirm
              </GuButton>
            </template>
          </GuDialogContent>
        </GuDialog>
      </div>

      <div v-else-if="status === COMPANY_STATUS.DISABLED">
        <GuButton
          aria-label="Enable Company"
          icon="bx-check"
          size="sm"
          variant="success"
          outline
          @click="triggerAction('enable')"
        >
          Enable
        </GuButton>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, toRefs } from 'vue'

import { GuButton } from '@shared/gu-components/button'
import { GuDialog, GuDialogClose, GuDialogContent, GuDialogTrigger } from '@shared/gu-components/dialog'
import { useToast } from '@shared/gu-components/toast'

import { approveCompany, deleteCompany, disableCompany, enableCompany } from '@companies/api/companies.api'
import { COMPANY_STATUS, MAP_RESULT_TO_STATUS } from '@companies/constants'

const props = defineProps({
  params: {
    type: Object,
    default: () => ({}),
  },
})
const { params } = toRefs(props)

const { toast } = useToast()

// v1 company accept/reject,enable/disable api returns
// {
//   "Status": // 'Approved', 'Enabled', 'Deleted', 'Disabled'
// }



const status = ref(params.value?.value || null)
const actionInProgress = ref(false)
const isConfirmationDialogOpen = ref(false)

const { mutateAsync: approveCompanyMutation } = approveCompany(params.value.data.name)
const { mutateAsync: deleteCompanyMutation } = deleteCompany(params.value.data.name)
const { mutateAsync: disableCompanyMutation } = disableCompany(params.value.data.name)
const { mutateAsync: enableCompanyMutation } = enableCompany(params.value.data.name)

async function triggerAction (action) {
  actionInProgress.value = true

  const actions = {
    approve: (company) => approveCompanyMutation(company),
    reject: (company) => deleteCompanyMutation(company),
    disable: (company) => disableCompanyMutation(company),
    enable: (company) => enableCompanyMutation(company),
  }

  try {
    const company = params.value.data.name

    const response = await actions[action](company)
    const rowNode = params.value.node
    const updateData = rowNode.data
    
    for (const [key, value] of Object.entries(response)) {
      updateData[key.toLowerCase()] = MAP_RESULT_TO_STATUS[value.toUpperCase()]
    }
    rowNode.updateData(updateData)
    
    isConfirmationDialogOpen.value = false
  } catch (ex) {
    console.error(ex)

    toast({
      variant: 'danger',
      description: ex.details || ex.message,
    })
  } finally {
    actionInProgress.value = false
  }
}
</script>
